import React, { useOptimistic, startTransition } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Account } from '@/types/accounts.types';
import { useActivateUserMutation, useDeactivateUserMutation } from '@/pages/hainok/Accounts/accountsApi';

type UsersDialogProps = {
  usersDialogOpen: boolean;
  setUsersDialogOpen: (open: boolean) => void;
  users: Account['users'];
  accountId?: string;
};

type OptimisticAction = {
  type: 'activate' | 'deactivate';
  userId: string;
};

const getUserStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'verified':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'registered':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'invited':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'unknown':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getRoleColor = (role: string) => {
  switch (role?.toLowerCase()) {
    case 'super-admin':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'account-admin':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'user':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const UsersDialog: React.FC<UsersDialogProps> = ({ usersDialogOpen, setUsersDialogOpen, users, accountId }) => {
  const [activateUser, { isLoading: isActivating }] = useActivateUserMutation();
  const [deactivateUser, { isLoading: isDeactivating }] = useDeactivateUserMutation();

  // Use optimistic updates for user activation status
  const [optimisticUsers, addOptimisticUpdate] = useOptimistic(users, (currentUsers, action: OptimisticAction) => {
    return currentUsers.map((user) => {
      return user.id === action.userId ? { ...user, isActive: action.type === 'activate' } : user;
    });
  });

  const handleUserActivation = (userId: string, currentIsActive: boolean) => {
    if (!accountId) {
      console.error('Account ID is required for user activation/deactivation');
      return;
    }

    const action: OptimisticAction = {
      type: currentIsActive ? 'deactivate' : 'activate',
      userId,
    };

    // Optimistically update the UI
    addOptimisticUpdate(action);

    // Perform the actual API call in a transition
    startTransition(async () => {
      try {
        if (currentIsActive) deactivateUser({ accountId, userId });
        else activateUser({ accountId, userId });
      } catch (error) {
        console.error('Failed to update user status:', error);
        // The error will be handled by RTK Query, and the cache will be invalidated
        // causing a refetch that will restore the correct state
      }
    });
  };

  return (
    <Dialog open={usersDialogOpen} onOpenChange={setUsersDialogOpen}>
      <DialogContent className='bg-slate-800 border-slate-700 text-white max-w-6xl w-[90vw] sm:max-w-6xl sm:w-[90vw] max-h-[80vh] overflow-hidden flex flex-col'>
        <DialogHeader>
          <DialogTitle>Account Users</DialogTitle>
        </DialogHeader>
        <div className='py-4 flex-1 overflow-hidden'>
          {optimisticUsers?.length > 0 ? (
            <div className='overflow-auto max-h-full'>
              <table className='w-full'>
                <thead>
                  <tr className='border-b border-slate-700'>
                    <th className='text-left py-3 px-4 text-slate-300 font-medium min-w-[250px]'>Email</th>
                    <th className='text-left py-3 px-4 text-slate-300 font-medium min-w-[200px]'>Name</th>
                    <th className='text-left py-3 px-4 text-slate-300 font-medium min-w-[120px]'>Role</th>
                    <th className='text-left py-3 px-4 text-slate-300 font-medium min-w-[100px]'>Status</th>
                    <th className='text-left py-3 px-4 text-slate-300 font-medium min-w-[80px]'>Active</th>
                    <th className='py-3 px-4 min-w-[100px]'></th>
                  </tr>
                </thead>
                <tbody>
                  {optimisticUsers.map((user) => (
                    <tr key={user.id} className='border-b border-slate-700/50 hover:bg-slate-700/25'>
                      <td className='py-3 px-4 min-w-[250px]'>
                        <div className='text-white break-all'>{user.email}</div>
                      </td>
                      <td className='py-3 px-4 min-w-[200px]'>
                        <div className='text-white'>
                          {user.firstName} {user.lastName}
                        </div>
                      </td>
                      <td className='py-3 px-4 min-w-[120px]'>
                        <Badge className={getRoleColor(user.role)}>{user.role}</Badge>
                      </td>
                      <td className='py-3 px-4 min-w-[100px]'>
                        <Badge className={getUserStatusColor(user.status)}>{user.status}</Badge>
                      </td>
                      <td className='py-3 px-4 min-w-[80px] border-r-2 border-solid border-slate-700'>
                        <Badge
                          variant='secondary'
                          className={`${user.isActive ? 'bg-green-500' : 'bg-red-900'} text-black`}
                        >
                          {user.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className='py-3 px-4 min-w-[100px]'>
                        <Button
                          size='sm'
                          variant={user.isActive ? 'destructive' : 'default'}
                          onClick={() => handleUserActivation(user.id, user.isActive)}
                          className='text-xs'
                          disabled={!accountId}
                        >
                          {user.isActive
                            ? `${isDeactivating ? 'Deactivating...' : 'Deactivate'}`
                            : `${isActivating ? 'Activating...' : 'Activate'}`}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='text-center py-8 text-slate-400'>No users found for this account</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
